import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLang } from "../contexts/LanguageContext";
import AppLayout from "../components/AppLayout";
import BottomNav from "../components/BottomNav";
import { Eye, Send, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignsPage() {
  const { isLoggedIn, api } = useAuth();
  const { theme } = useTheme();
  const { t, lang } = useLang();
  const isDark = theme === "dark";
  const [description, setDescription] = useState("");
  const [interpretation, setInterpretation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const textPrimary = isDark ? "text-white" : "text-slate-900";
  const textSecondary = isDark ? "text-slate-300/80" : "text-slate-600";
  const inputBg = isDark ? "bg-slate-950/30 border-white/10 text-white placeholder:text-slate-500" : "bg-white/50 border-slate-200 text-slate-900 placeholder:text-slate-400";

  const handleInterpret = async () => {
    if (!description.trim()) return;
    setLoading(true); setError(""); setInterpretation(null);
    try {
      let res;
      if (isLoggedIn) { res = await api().post("/signs/interpret", { description, lang }); }
      else { res = await axios.post(`${API}/signs/interpret/guest`, { description, lang }); }
      setInterpretation(res.data.interpretation);
    } catch (err) { setError(err.response?.data?.detail || t("error")); }
    finally { setLoading(false); }
  };

  return (
    <AppLayout>
      <div className="px-5 pt-12 pb-24 scrollbar-hide overflow-y-auto min-h-screen">
        <div className="mb-6 animate-fadeInUp">
          <h1 className={`font-heading text-3xl font-bold ${textPrimary} ${isDark ? "drop-shadow-lg" : ""}`}>{t("signsTitle")}</h1>
          <p className={`${textSecondary} text-sm mt-1`}>{t("signsSub")}</p>
        </div>
        <div className="glass-card p-5 animate-fadeInUp">
          <div className="flex items-center gap-2 mb-3">
            <Eye size={14} className="text-blue-500" />
            <label className={`text-[10px] ${isDark ? "text-blue-400" : "text-blue-600"} uppercase tracking-[0.15em] font-semibold`}>{t("describeSign")}</label>
          </div>
          <textarea data-testid="sign-input-textarea" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("signPlaceholder")}
            className={`w-full border rounded-xl p-4 min-h-[140px] focus:border-blue-500/50 outline-none transition-all resize-none text-sm leading-relaxed ${inputBg}`} />
          <button data-testid="sign-submit-button" onClick={handleInterpret} disabled={loading || !description.trim()}
            className="mt-3 w-full btn-gold py-3.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
            {loading ? <><Loader2 size={18} className="animate-spin" /> {t("analyzing")}</> : <><Send size={16} /> {t("interpretSign")}</>}
          </button>
        </div>
        {error && <div className="mt-4 glass-card p-4"><p className="text-red-500 text-sm">{error}</p></div>}
        {interpretation && (
          <div data-testid="sign-interpretation-result" className="mt-5 glass-card p-6 animate-fadeInUp">
            <div className="flex items-center gap-2 mb-4"><Eye size={14} className="text-blue-500" /><p className={`text-[10px] ${isDark?"text-blue-400":"text-blue-600"} uppercase tracking-[0.15em] font-semibold`}>{t("meaning")}</p></div>
            <div className={`${textPrimary} text-sm leading-relaxed whitespace-pre-wrap`}>{interpretation}</div>
          </div>
        )}
      </div>
      <BottomNav />
    </AppLayout>
  );
}
